import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, redirect, useActionData, useLoaderData, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCustomerAddresses } from '../../api/customer';
import { bulkUploadProjectItemAddresses, getProjectItemAddresses, getProjectItemDetails, updateProjectItemAddresses } from '../../api/project';
import { Image, Message, MessageTypes } from '../../components/ui';
import i18n from '../../i18n.js';
import { ActionError, ValidationError } from '../../library/classes/exceptions';
import { getCmsFileUrl } from '../../library/constants';
import { generateRouteUrl } from '../../library/constants/routes.jsx';
import { getCountryById } from '../../library/helper/country.js';
import { BulkDeliveryAddresses } from './bulk-delivery-addresses.jsx';
import { MultipleDeliveryAddresses } from './multiple-delivery-addresses.jsx';
import { SingleDeliveryAddress } from './single-delivery-address.jsx';
import styles from './project-item-address.module.scss';

const ADDRESS_TYPES = {
    SINGLE: "single",
    MULTIPLE: "multiple",
    BULK: "bulk"
}

const ProjectItemAddress = () => {
    const { quotationToken } = useParams()
    const { t } = useTranslation()
    const { projectItem, itemAddresses, customerAddresses } = useLoaderData()
    const [deliveriesCount, setDeliveriesCount] = useState(itemAddresses?.length)
    const [selectedAddresses, setSelectedAddresses] = useState([...itemAddresses])
    const [addressType, setAddressType] = useState(null);

    // Use a single useEffect to determine address type
    useEffect(() => {
        // First priority: delivery_type if it's 3 (BULK)
        if (projectItem.delivery_type === 3) {
            setAddressType(ADDRESS_TYPES.BULK);
        }
        // Second priority: If not confirmed by client, use deliveriesCount
        else if (!projectItem.client_confirm_address) {
            setAddressType(deliveriesCount > 5 ? ADDRESS_TYPES.BULK :
                deliveriesCount > 1 ? ADDRESS_TYPES.MULTIPLE :
                    ADDRESS_TYPES.SINGLE);
        }
        // Third priority: Use itemAddresses.length
        else if (itemAddresses?.length > 1) {
            setAddressType(ADDRESS_TYPES.MULTIPLE);
        }
        // Default
        else {
            setAddressType(ADDRESS_TYPES.SINGLE);
        }
    }, [projectItem.delivery_type, itemAddresses, deliveriesCount, projectItem.client_confirm_address]);

    const actionData = useActionData()

    const handleAddressChange = useCallback((addresses) => {
        setSelectedAddresses(addresses.map(address => ({
            ...customerAddresses.find(({ id }) => id === address.address_id),
            ...address
        })))
    }, [])

    const remainingQuantity = useMemo(() => {
        return selectedAddresses.reduce((acc, curr) => ((!!curr.address_id && curr.quantity > 0) ? acc - curr.quantity : acc), projectItem.quantity)
    }, [selectedAddresses])

    return (
        <div className="wrapper addresses-wrapper">
            <div className="main">
                <h2 className="main--title">
                    <img src="/icons/add-location.svg" alt="" />
                    {t("Delivery Address")}
                </h2>
                <div className="box--white">
                    <div className={styles.header}>
                        <h2>{projectItem.product.name}</h2>
                        <Link to={generateRouteUrl('Dashboard', { quotationToken })} className="button"><img src="/icons/arrow.svg" alt="back" />{t("Back")}</Link>
                    </div>
                    <div className={styles.content}>
                        <div className={styles.contentLeft}>
                            <Image src={getCmsFileUrl(projectItem.product?.main_photos?.[0])} width={300} height={200} className={styles.productImage} />
                            <div className="box--small box--light-gray"><strong>{t("Approved Quantity")}</strong> : {projectItem.quantity}</div>
                            {addressType === ADDRESS_TYPES.MULTIPLE && (
                                <>
                                    <hr />
                                    <h3 style={{ padding: 'var(--space-small)' }}>{t('Summary')}</h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        {selectedAddresses.map((address, index) => ({ address, number: index + 1 })).filter(({ address }) => !!address.address_id).map(({ address, number }) => (
                                            <div key={address.id ?? number} className="box--light-gray box--small">
                                                <div className="box__heading"><strong>{t('Delivery')} {number} · {t('Quantity')}: {address?.quantity}</strong></div>
                                                <span style={{ lineHeight: "1.4" }}>
                                                    {[
                                                        address.street,
                                                        address.street_number,
                                                        address.city,
                                                        address.county,
                                                        getCountryById(address.country_id)?.name,
                                                        address.zipcode,
                                                    ].filter(Boolean).join(', ')}
                                                </span>
                                            </div>
                                        ))}
                                        {remainingQuantity > 0 && (
                                            <div className='error-message' style={{ padding: '0 var(--space-small)' }}>{t('Remaining Quantity')}: {remainingQuantity}</div>
                                        )}
                                        {remainingQuantity < 0 && (
                                            <div className='error-message' style={{ padding: '0 var(--space-small)' }}>{t('Quantity exceeds ordered quantity')}: {-remainingQuantity}</div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        {<div className={styles.contentRight}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} >
                                {/* {t("Delivery Address")} */}
                                {!projectItem.client_confirm_address && (
                                    <div className="box--light-gray">
                                        <p>{t("Select number of deliveries required")}</p>
                                        <div className={styles.countField}>
                                            <input type="number" className={styles.countInput} inputMode="numeric" value={deliveriesCount} min={1} step={1} onChange={e => {
                                                if (isNaN(parseInt(e.target.value))) {
                                                    setDeliveriesCount(1)
                                                }
                                                else {
                                                    setDeliveriesCount(Math.max(1, parseInt(e.target.value)))
                                                }
                                            }} />
                                            {/* phones don't draw number-field arrows; these replace them on touch screens */}
                                            <div className={styles.countArrows}>
                                                <button type="button" aria-label="+" onClick={() => setDeliveriesCount(count => Math.max(1, count + 1))}>▲</button>
                                                <button type="button" aria-label="-" disabled={deliveriesCount <= 1} onClick={() => setDeliveriesCount(count => Math.max(1, count - 1))}>▼</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {!!actionData && actionData instanceof ActionError && <Message type={MessageTypes.ERROR} message={actionData.message} timeOut={3} />}
                                {actionData === true && <Message type={MessageTypes.SUCCESS} message="Address updated successfully" timeOut={3} />}
                                {addressType === ADDRESS_TYPES.SINGLE && <SingleDeliveryAddress isConfirmed={projectItem.client_confirm_address != null} customerAddresses={customerAddresses} selectedAddress={itemAddresses.map(({ address_id, notes, quantity }) => ({ address_id, notes, quantity }))?.[0]} onChange={handleAddressChange} />}
                                {addressType === ADDRESS_TYPES.MULTIPLE && <MultipleDeliveryAddresses isConfirmed={projectItem.client_confirm_address != null} customerAddresses={customerAddresses} itemAddresses={itemAddresses.map(({ address_id, notes, quantity }) => ({ address_id, notes, quantity }))} totalQuantity={projectItem.approved_quantity} addressesCount={deliveriesCount} onChange={handleAddressChange} />}
                                {addressType === ADDRESS_TYPES.BULK && <BulkDeliveryAddresses isConfirmed={projectItem.client_confirm_address != null} uploadedFile={projectItem.delivery_addresses?.deliveries?.file} quotationToken={quotationToken} />}
                            </div>
                        </div>}
                    </div>
                </div>
            </div>
            <Outlet context={{
                customerCountryId: projectItem?.customer?.country_id,
                customerCompany: projectItem?.customer?.company
            }} />
        </div>
    )
}

ProjectItemAddress.Actions = {
    singleDeliveryAddress: async ({ params, data }) => {
        const { quotationToken, itemId } = params
        const { address_id, notes } = data
        try {
            if (!address_id || !parseInt(address_id)) {
                return {
                    error: [{
                        path: 'address_id',
                        message: 'Address is required'
                    }]
                }
            }
            const parsedAddresses = [{
                address_id: parseInt(address_id),
                notes,
            }]
            await updateProjectItemAddresses(quotationToken, itemId, parsedAddresses)
            return redirect(generateRouteUrl('Dashboard', { quotationToken }))
        }
        catch (e) {
            return e
        }

    },
    multipleDeliveryAddress: async ({ params, data }) => {
        const { quotationToken, itemId } = params
        const { addresses } = data

        try {
            const parsedAddresses = Object.values(addresses).map(({ address_id, quantity, notes }) => ({
                address_id: parseInt(address_id),
                quantity: parseInt(quantity),
                notes
            }))
            //Check if all addresses and quantities are filled
            parsedAddresses.forEach(({ address_id, quantity }, index) => {
                if (!address_id) {
                    throw new ValidationError('Address is required', [{
                        path: `addresses[${index}][address_id]`,
                        message: 'Please select an address'
                    }])
                }
                if (!quantity) {
                    throw new ValidationError('Quantity is required', [{
                        path: `addresses[${index}][quantity]`,
                        message: 'Please select a quantity'
                    }])
                }
            })
            await updateProjectItemAddresses(quotationToken, itemId, parsedAddresses)
            return redirect(generateRouteUrl('Dashboard', { quotationToken }))

        }
        catch (error) {
            return error
        }
    },
    bulkDeliveryAddress: async ({ params, data }) => {
        const { quotationToken, itemId } = params
        const { fileId } = data
        try {
            await bulkUploadProjectItemAddresses(quotationToken, itemId, fileId)
            toast.success(i18n.t("File Uploaded successfully"));
            return redirect(generateRouteUrl('Dashboard', { quotationToken }))

        }
        catch (e) {
            toast.error(e?.message)
            return { error: e }
        }

    },
}

ProjectItemAddress.Loader = async ({ params }) => {
    const { quotationToken, itemId } = params
    const projectItem = await getProjectItemDetails(quotationToken, itemId)
    const itemAddresses = await getProjectItemAddresses(quotationToken, itemId)
    const customerAddresses = await getCustomerAddresses(quotationToken)
    return { projectItem, itemAddresses, customerAddresses }
}

export default ProjectItemAddress
